import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../apiClient";

export default function AdminDashboard() {
  const navigate = useNavigate();

  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastType, setToastType] = useState("dark"); // success | danger | warning | info | dark

  const admin_username = sessionStorage.getItem("admin_username");

  const [title, setTitle] = useState("");
  const [quizCode, setQuizCode] = useState("");

  const [question, setQuestion] = useState("");
  const [opt1, setOpt1] = useState("");
  const [opt2, setOpt2] = useState("");
  const [opt3, setOpt3] = useState("");
  const [opt4, setOpt4] = useState("");
  const [answer, setAnswer] = useState("");

  const [questions, setQuestions] = useState([]);
  const [editId, setEditId] = useState(null);

  // For delete confirmation (no window.confirm)
  const [deleteId, setDeleteId] = useState(null);

  const showToastMessage = (message, type = "dark", duration = 2000) => {
    setToastMsg(message);
    setToastType(type);
    setShowToast(true);

    setTimeout(() => setShowToast(false), duration);
  };

  useEffect(() => {
    const token = sessionStorage.getItem("admin_token");
    if (!token) navigate("/admin-login");
    loadQuestions();
  }, [navigate]);

  const loadQuestions = async () => {
    try {
      const res = await apiClient.get("/getQuestionsByAdmin.php");
      setQuestions(res.data || []);
    } catch (err) {
      setQuestions([]);
      showToastMessage("❌ Failed to load questions", "danger");
    }
  };

  const createQuiz = async () => {
    if (!title.trim()) {
      showToastMessage("⚠️ Please enter quiz title!", "warning");
      return;
    }

    try {
      const res = await apiClient.post("/createNewQuiz.php", { title });

      if (res.data.status === "success") {
        setQuizCode(res.data.quiz_code);
        setQuestions([]);
        showToastMessage("✅ Quiz created! Now add questions.", "success", 2500);
      } else {
        showToastMessage(`❌ ${res.data.message}`, "danger", 2500);
      }
    } catch (err) {
      showToastMessage("❌ Failed to create quiz (Token invalid / Server error)", "danger", 2500);
    }
  };

  const clearForm = () => {
    setQuestion("");
    setOpt1("");
    setOpt2("");
    setOpt3("");
    setOpt4("");
    setAnswer("");
  };

  const addOrUpdateQuestion = async () => {
    if (!question.trim() || !answer.trim()) {
      showToastMessage("⚠️ Question and Correct Answer required!", "warning");
      return;
    }

    try {
      if (editId) {
        const res = await apiClient.post("/updateQuestion.php", {
          id: editId,
          question,
          opt1,
          opt2,
          opt3,
          opt4,
          answer,
        });

        showToastMessage(`✅ ${res.data.message}`, "success", 2000);
        setEditId(null);
      } else {
        const res = await apiClient.post("/addQuestion.php", {
          question,
          opt1,
          opt2,
          opt3,
          opt4,
          answer,
        });

        showToastMessage(`✅ ${res.data.message}`, "success", 2000);
      }

      clearForm();
      loadQuestions();
    } catch (err) {
      showToastMessage("❌ Failed! (Token invalid / Server error)", "danger", 2500);
    }
  };

  const requestDelete = (id) => {
    setDeleteId(id);
    showToastMessage("🗑️ Confirm delete below (Yes / No)", "warning", 4000);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    try {
      const res = await apiClient.post("/deleteQuestion.php", { id: deleteId });
      showToastMessage(`✅ ${res.data.message}`, "success", 2000);
      setDeleteId(null);
      loadQuestions();
    } catch (err) {
      showToastMessage("❌ Delete failed!", "danger", 2500);
    }
  };

  const cancelDelete = () => {
    setDeleteId(null);
    showToastMessage("❎ Delete cancelled", "info", 1500);
  };

  const editQuestionRow = (q) => {
    setEditId(q.id);
    setQuestion(q.question);
    setOpt1(q.opt1);
    setOpt2(q.opt2);
    setOpt3(q.opt3);
    setOpt4(q.opt4);
    setAnswer(q.answer);

    showToastMessage("✏️ Edit mode enabled", "info", 1500);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const logout = () => {
    sessionStorage.removeItem("admin_token");
    sessionStorage.removeItem("admin");
    sessionStorage.removeItem("admin_username");
    showToastMessage("👋 Logged out successfully", "info", 1500);
    setTimeout(() => navigate("/"), 800);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToastMessage("✅ Quiz link copied to clipboard!", "success", 2000);
    } catch (err) {
      showToastMessage("❌ Copy failed! Please copy manually.", "danger", 2500);
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="fw-bold mb-0">Admin Dashboard</h2>
          <small className="text-muted">Logged in as: {admin_username}</small>
        </div>
        <button className="btn btn-danger" onClick={logout}>
          Logout
        </button>
      </div>

      {/* Create Quiz */}
      <div className="card shadow-sm border-0 rounded-4 mb-4">
        <div className="card-body p-4">
          <h4 className="fw-bold mb-3">Create New Quiz</h4>

          <input
            className="form-control form-control-lg mb-3"
            placeholder="Enter Quiz Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <button className="btn btn-warning btn-lg" onClick={createQuiz}>
            Create Quiz (Clears Old Quiz)
          </button>

          {quizCode && (
            <div className="alert alert-success mt-4">
              <b>Quiz Created Successfully!</b>

              <div className="mt-2">
                <label className="form-label fw-semibold">Share Link:</label>

                <div className="input-group">
                  <input
                    className="form-control"
                    readOnly
                    value={`${window.location.origin}/?quiz=${quizCode}`}
                    style={{ cursor: "pointer" }}
                    title="Click to copy"
                    onClick={(e) => copyToClipboard(e.target.value)}
                  />

                  <button
                    className="btn btn-success"
                    type="button"
                    onClick={() =>
                      copyToClipboard(`${window.location.origin}/?quiz=${quizCode}`)
                    }
                  >
                    Copy
                  </button>
                </div>

                <small className="text-muted d-block mt-1">
                  Click the input box or press Copy button.
                </small>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Question */}
      <div className="card shadow-sm border-0 rounded-4 mb-4">
        <div className="card-body p-4">
          <h4 className="fw-bold mb-3">{editId ? "Edit Question" : "Add Question"}</h4>

          <input
            className="form-control mb-2"
            placeholder="Enter Question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />

          <div className="row g-2">
            <div className="col-md-6">
              <input
                className="form-control"
                placeholder="Option A"
                value={opt1}
                onChange={(e) => setOpt1(e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <input
                className="form-control"
                placeholder="Option B"
                value={opt2}
                onChange={(e) => setOpt2(e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <input
                className="form-control"
                placeholder="Option C"
                value={opt3}
                onChange={(e) => setOpt3(e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <input
                className="form-control"
                placeholder="Option D"
                value={opt4}
                onChange={(e) => setOpt4(e.target.value)}
              />
            </div>
          </div>

          <input
            className="form-control mt-2"
            placeholder="Correct Answer (must match option text)"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
          />

          <div className="d-flex flex-column flex-md-row gap-2 mt-3">
            <button className="btn btn-primary btn-lg" onClick={addOrUpdateQuestion}>
              {editId ? "Update Question" : "Add Question"}
            </button>

            {editId && (
              <button
                className="btn btn-secondary btn-lg"
                onClick={() => {
                  setEditId(null);
                  clearForm();
                  showToastMessage("❎ Edit cancelled", "info", 1500);
                }}
              >
                Cancel Edit
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Bar */}
      {deleteId && (
        <div className="alert alert-warning shadow-sm d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
          <div>
            <b>Are you sure?</b> Delete Question ID: <b>{deleteId}</b>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-danger" onClick={confirmDelete}>
              Yes Delete
            </button>
            <button className="btn btn-secondary" onClick={cancelDelete}>
              No
            </button>
          </div>
        </div>
      )}

      {/* Questions Table */}
      <div className="card shadow-sm border-0 rounded-4">
        <div className="card-body p-4">
          <h4 className="fw-bold mb-3">Your Questions</h4>

          {questions.length === 0 ? (
            <div className="alert alert-info">No questions found. Add questions now.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered align-middle">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Question</th>
                    <th>Answer</th>
                    <th style={{ width: "180px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((q) => (
                    <tr key={q.id}>
                      <td>{q.id}</td>
                      <td>{q.question}</td>
                      <td>{q.answer}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-info me-2"
                          onClick={() => editQuestionRow(q)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => requestDelete(q.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      <div
        className={`toast align-items-center text-bg-${toastType} border-0 position-fixed bottom-0 end-0 m-3 ${
          showToast ? "show" : "hide"
        }`}
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        style={{ zIndex: 9999 }}
      >
        <div className="d-flex">
          <div className="toast-body">{toastMsg}</div>
          <button
            type="button"
            className="btn-close btn-close-white me-2 m-auto"
            aria-label="Close"
            onClick={() => setShowToast(false)}
          ></button>
        </div>
      </div>
    </div>
  );
}
